import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Brain, TrendingUp } from 'lucide-react';
import { DIMENSIONS, NOTE_FREQUENCIES } from '../constants/gameConstants';
import { useAudioContext } from '../hooks/useAudioContext';
import { useGameAudio } from '../hooks/useGameAudio';
import { useRhythm } from '../hooks/useRhythm';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { favoritesApi } from '../utils/api';
// import { useMandarinTones } from '../hooks/useMandarinTones'; // No longer needed - using voice synthesis
import {
  generateMatchSchedule,
  generateStimulus,
  checkMatch,
  calculateLevelRecommendation,
  getDimensionComboKey
} from '../../utils/gameLogic';
import SettingsPanel from './game/SettingsPanel';
import CountdownScreen from './game/CountdownScreen';
import GameArea from './game/GameArea';
import RoundSummary from './game/RoundSummary';
import { globalStyles } from '../../styles/gameStyles';

const NBackGame = ({ initialDimensionLevels = {}, onRoundComplete, onReturnToSettings, trainedCombos = [], onGameStateChange, progressData = null }) => {
  // Auth context for favorites
  const { user, isAuthenticated } = useAuth();
  const { tier, tierRules, canAccessDimension } = useSubscription();
  const [favorites, setFavorites] = useState([]);
  
  // Load favorites on mount
  useEffect(() => {
    if (isAuthenticated) {
      loadFavorites();
    }
  }, [isAuthenticated]);
  
  const loadFavorites = async () => {
    try {
      const response = await favoritesApi.getFavorites();
      setFavorites(response.favorites || []);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };
  
  const toggleFavorite = async (comboCode, comboName) => {
    if (!isAuthenticated) return;
    
    try {
      const isFavorited = favorites.some(f => f.comboCode === comboCode);
      
      if (isFavorited) {
        await favoritesApi.removeFavorite(comboCode);
        setFavorites(favorites.filter(f => f.comboCode !== comboCode));
      } else {
        const response = await favoritesApi.addFavorite(comboCode, comboName);
        setFavorites(response.favorites || []);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };
  
  // Game configuration - initialize empty, will be set by useEffect
  const [activeDimensions, setActiveDimensions] = useState([]);
  const [nValue, setNValue] = useState(1);
  const [interferenceRate, setInterferenceRate] = useState(0);
  const [dimensionLevels, setDimensionLevels] = useState(initialDimensionLevels);
  const [totalTrials, setTotalTrials] = useState(22);
  const opportunityTrials = 20;

  // Game state
  const [isPlaying, setIsPlaying] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [showSummary, setShowSummary] = useState(false);
  const [roundSummary, setRoundSummary] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [currentTrial, setCurrentTrial] = useState(0);
  const [stimulusVisible, setStimulusVisible] = useState(true);

  // Notify parent of game state changes
  useEffect(() => {
    if (onGameStateChange) {
      if (showSettings && !isPlaying && !showSummary && countdown === null) {
        onGameStateChange('settings');
      } else if (isPlaying && !showSummary) {
        onGameStateChange('playing');
      } else if (showSummary && !showSettings && !isPlaying) {
        onGameStateChange('summary');
      }
    }
  }, [showSettings, isPlaying, showSummary, countdown, onGameStateChange]);

  // Current stimulus
  const [currentStimulus, setCurrentStimulus] = useState({
    position: 4,
    audio: 'C',
    voice: 'male1',
    note: 'C4',
    color: '#3b82f6',
    shape: 'circle',
    number: '5',
    face: '😀',
    arrow: '↑',
    movement: 'up',
    pattern: 'solid',
    border: 'top',
    orbitalPosition: 'N',
    circleSize: 'medium',
    circleRotation: 0,
    circleBrightness: 1.0,
    categoryText: 'DOG',
    categoryAudio: 'DOG',
    nameText: 'ANNA',
    nameAudio: 'ANNA',
    math: 0,
    mathDisplay: '1-1',
    rhythm: 0,
    mandarinTone: '妈'
  });

  // Settings for audio flavors - no longer needed
  // const [useMandarinTones, setUseMandarinTones] = useState(false);

  // History and responses
  const [history, setHistory] = useState([]);
  const [responses, setResponses] = useState({});
  const [accuracy, setAccuracy] = useState({});
  const accuracyRef = useRef({});
  const responsesRef = useRef({});
  const [matchSchedule, setMatchSchedule] = useState({});

  // Helper function to check if combo is allowed for current tier
  const isComboAllowed = useCallback((comboCode) => {
    const codeToMapping = {
      'P': 'position', 'A': 'audio', 'V': 'voice', 'M': 'note',
      'C': 'color', 'S': 'shape', 'N': 'number', 'F': 'face',
      'R': 'arrow', 'O': 'movement', 'T': 'pattern', 'B': 'border',
      'L': 'orbitalPosition', 'Z': 'circleSize', 'X': 'circleRotation', 'I': 'circleBrightness',
      'W': 'categoryText', 'E': 'categoryAudio', 'Y': 'nameText', 'U': 'nameAudio', 
      'Q': 'math', 'H': 'rhythm', 'D': 'mandarinTone', 'G': 'volume'
    };
    
    const dimensions = comboCode.split('').map(code => codeToMapping[code]).filter(Boolean);
    
    // Check if combo has too many dimensions
    if (dimensions.length > tierRules.maxDimensions) {
      return false;
    }
    
    // Check if all dimensions are allowed
    return dimensions.every(dim => canAccessDimension(dim));
  }, [tier, tierRules, canAccessDimension]);

  // Helper function to get the most recently trained ALLOWED combo
  const getMostRecentAllowedCombo = useCallback(() => {
    if (trainedCombos.length === 0) {
      return []; // No combos - user should select manually
    }
    
    // Filter to only allowed combos
    const allowedCombos = trainedCombos.filter(combo => isComboAllowed(combo.comboCode));
    
    if (allowedCombos.length === 0) {
      return []; // No allowed combos - user should select manually
    }
    
    // Sort by lastTrained date (most recent first)
    const sortedCombos = [...allowedCombos].sort((a, b) => {
      const dateA = new Date(a.lastTrained || 0);
      const dateB = new Date(b.lastTrained || 0);
      return dateB - dateA;
    });
    
    const mostRecentCombo = sortedCombos[0];
    
    // Convert comboCode to dimension names
    const codeToMapping = {
      'P': 'position', 'A': 'audio', 'V': 'voice', 'M': 'note',
      'C': 'color', 'S': 'shape', 'N': 'number', 'F': 'face',
      'R': 'arrow', 'O': 'movement', 'T': 'pattern', 'B': 'border',
      'L': 'orbitalPosition', 'Z': 'circleSize', 'X': 'circleRotation', 'I': 'circleBrightness',
      'W': 'categoryText', 'E': 'categoryAudio', 'Y': 'nameText', 'U': 'nameAudio', 
      'Q': 'math', 'H': 'rhythm', 'D': 'mandarinTone', 'G': 'volume'
    };
    
    const dimensions = mostRecentCombo.comboCode.split('').map(code => codeToMapping[code]).filter(Boolean);
    
    return dimensions.length > 0 ? dimensions : [];
  }, [trainedCombos, tier, tierRules, isComboAllowed, canAccessDimension]);

  // Hooks for audio and voice
  const { playNote, playToneWithVolume } = useAudioContext();
  const { voiceMap, playAudio, playCategoryAudio, playNameAudio, loadingStatus, hasMP3Files } = useGameAudio();
  const { playRhythm, stopRhythm } = useRhythm();
  // Mandarin tones use playAudio with Chinese voice (TTS only for now)

  // Create a key-to-dimension lookup map for faster keypress handling
  const keyToDimensionRef = useRef({});
  useEffect(() => {
    // Build lookup map once
    const keyMap = {};
    Object.keys(DIMENSIONS).forEach(dim => {
      keyMap[DIMENSIONS[dim].key] = dim;
    });
    keyToDimensionRef.current = keyMap;
  }, []);
  
  // Use ref for currentTrial to avoid recreating event listener every trial
  const currentTrialRef = useRef(currentTrial);
  useEffect(() => {
    currentTrialRef.current = currentTrial;
  }, [currentTrial]);

  // Sync dimension levels when prop changes (but not during active play, summary, or countdown)
  useEffect(() => {
    if (!isPlaying && !showSummary && countdown === null) {
      setDimensionLevels(initialDimensionLevels);
    }
  }, [initialDimensionLevels, isPlaying, showSummary, countdown]);

  // Initialize and update activeDimensions with most recent ALLOWED combo
  useEffect(() => {
    // Only set initial dimensions on first mount or when returning to settings
    if (showSettings && !isPlaying && !showSummary && countdown === null) {
      const allowedDimensions = getMostRecentAllowedCombo();
      
      // Only update if we have a valid result and it's different from current
      if (allowedDimensions.length > 0 || activeDimensions.length > 0) {
        const currentKey = [...activeDimensions].sort().join(',');
        const newKey = [...allowedDimensions].sort().join(',');
        
        if (currentKey !== newKey) {
          setActiveDimensions(allowedDimensions);
        }
      }
    }
  }, [trainedCombos, tier, showSettings, isPlaying, showSummary, countdown, getMostRecentAllowedCombo]);

  // Track active dimensions for level loading
  const prevActiveDimensionsRef = useRef(null);
  
  useEffect(() => {
    const currentKey = [...activeDimensions].sort().join(',');
    const prevKey = prevActiveDimensionsRef.current;
    
    // Only update nValue if dimensions actually changed AND we're in settings mode
    if (currentKey !== prevKey && activeDimensions.length > 0 && !isPlaying && !showSummary && countdown === null && showSettings) {
      const levelForCombo = dimensionLevels[currentKey] || 1;
      setNValue(levelForCombo);
      prevActiveDimensionsRef.current = currentKey;
    }
  }, [activeDimensions, isPlaying, dimensionLevels, showSummary, countdown, showSettings]);

  // Get level for current dimension combination
  const getLevelForCombo = useCallback((dimensions) => {
    const key = getDimensionComboKey(dimensions);
    return dimensionLevels[key] || 1;
  }, [dimensionLevels]);

  // Check if current combo has been trained
  const isComboTrained = useCallback(() => {
    const key = getDimensionComboKey(activeDimensions);
    return dimensionLevels[key] !== undefined;
  }, [activeDimensions, dimensionLevels]);

  // Update level for specific dimension combination
  const updateLevelForCombo = useCallback((dimensions, newLevel) => {
    const key = getDimensionComboKey(dimensions);
    setDimensionLevels(prev => ({
      ...prev,
      [key]: newLevel
    }));
  }, []);

  // Countdown before game starts
  useEffect(() => {
    if (countdown === null) return;
    
    // Notify parent of game state
    if (onGameStateChange) {
      onGameStateChange('countdown');
    }
    
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Countdown finished, start game
      setCountdown(null);
      setIsPlaying(true);
      setStimulusVisible(false);
    }
  }, [countdown, onGameStateChange]);

  // Handle key press
  useEffect(() => {
    if (!isPlaying) return;

    const handleKeyPress = (e) => {
      const key = e.key.toLowerCase();
      const dimension = keyToDimensionRef.current[key]; // Fast O(1) lookup
      
      if (dimension && activeDimensions.includes(dimension)) {
        const trialKey = `${currentTrialRef.current}_${dimension}`; // Use ref instead of state
        if (!responsesRef.current[trialKey]) {
          responsesRef.current[trialKey] = true;
          // No state update needed - ref is sufficient for game logic
          // This eliminates ALL lag from key presses
        }
      }
    };

    // Use passive listener for better performance (doesn't block scrolling/rendering)
    window.addEventListener('keydown', handleKeyPress, { passive: true });
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isPlaying, activeDimensions]); // Removed currentTrial from dependencies!

  // Handler for touch button presses (tablet/mobile)
  // Track which dimensions have been pressed in the current trial (for UI feedback)
  const [pressedInCurrentTrial, setPressedInCurrentTrial] = useState(new Set());
  
  const handleDimensionPress = useCallback((dimension) => {
    if (!isPlaying || !activeDimensions.includes(dimension)) return;
    
    const trialKey = `${currentTrialRef.current}_${dimension}`;
    if (!responsesRef.current[trialKey]) {
      responsesRef.current[trialKey] = true;
      
      // Mark this dimension as pressed in current trial for UI feedback
      setPressedInCurrentTrial(prev => new Set([...prev, dimension]));
    }
  }, [isPlaying, activeDimensions]);

  // Game loop
  useEffect(() => {
    if (!isPlaying) return;

    const initialDelay = currentTrial === 0 ? 1000 : 0;
    
    const startTimer = setTimeout(() => {
      setStimulusVisible(true);
      
      // Play audio for current trial
      // Check if volume dimension is active and get volume multiplier
      const volumeMultiplier = activeDimensions.includes('volume') ? currentStimulus.volume : 1.0;
      
      if (activeDimensions.includes('rhythm')) {
        // Rhythm: play pattern with note (or default note if note dimension not active)
        const noteFreq = activeDimensions.includes('note')
          ? NOTE_FREQUENCIES[currentStimulus.note]
          : NOTE_FREQUENCIES['C4']; // Default to C4 if note not active
        playRhythm(currentStimulus.rhythm, noteFreq, volumeMultiplier);
      } else if (activeDimensions.includes('note')) {
        playNote(currentStimulus.note, volumeMultiplier);
      } else if (activeDimensions.includes('mandarinTone')) {
        // Mandarin Tone dimension - speak Chinese character with Chinese voice
        // Try to use a Chinese voice (zh-CN), fallback to default
        const chineseVoice = 'zh-CN'; // Browser will try to use Chinese TTS
        playAudio(currentStimulus.mandarinTone, chineseVoice, false, volumeMultiplier);
      } else if (activeDimensions.includes('audio')) {
        if (activeDimensions.includes('voice')) {
          const isVoiceOnly = !activeDimensions.includes('audio');
          playAudio(currentStimulus.audio, currentStimulus.voice, isVoiceOnly, volumeMultiplier);
        } else {
          playAudio(currentStimulus.audio, 'male1', false, volumeMultiplier);
        }
      } else if (activeDimensions.includes('voice')) {
        playAudio(currentStimulus.audio, currentStimulus.voice, true, volumeMultiplier);
      } else if (activeDimensions.includes('categoryAudio')) {
        // Play category word (uses MP3 files when available, falls back to TTS)
        playCategoryAudio(currentStimulus.categoryAudio.toLowerCase(), 'female2', volumeMultiplier);
      } else if (activeDimensions.includes('nameAudio')) {
        // Play name (uses TTS for now, can add MP3s later)
        playNameAudio(currentStimulus.nameAudio.toLowerCase(), 'female2', volumeMultiplier);
      } else if (activeDimensions.includes('volume')) {
        // Volume dimension alone: play middle C at specified volume
        playToneWithVolume(currentStimulus.volume);
      }
    }, initialDelay);

    const hideTimer = setTimeout(() => {
      setStimulusVisible(false);
    }, 2000 + initialDelay);

    const advanceTimer = setTimeout(() => {
      // Calculate and store accuracy for this trial
      if (currentTrial >= nValue) {
        const newAccuracy = {...accuracyRef.current};
        activeDimensions.forEach(dimension => {
          const trialKey = `${currentTrial}_${dimension}`;
          const responded = responsesRef.current[trialKey] || false;
          const shouldMatch = checkMatch(dimension, currentStimulus, history, nValue);
          
          const isCorrect = (responded && shouldMatch) || (!responded && !shouldMatch);
          
          if (!newAccuracy[dimension]) {
            newAccuracy[dimension] = [];
          }
          newAccuracy[dimension] = [...newAccuracy[dimension], isCorrect];
        });
        
        accuracyRef.current = newAccuracy;
        setAccuracy(newAccuracy);
        
        // Check if this was the last trial
        if (currentTrial >= totalTrials - 1) {
          setIsPlaying(false);
          calculateLevel(newAccuracy);
          return;
        }
      } else if (currentTrial >= totalTrials - 1) {
        setIsPlaying(false);
        calculateLevel();
        return;
      }

      // Clear pressed buttons state for next trial
      setPressedInCurrentTrial(new Set());
      
      // Next trial
      const newHistory = [...history, currentStimulus];
      setHistory(newHistory);
      const newStimulus = generateStimulus(
        currentTrial + 1,
        matchSchedule,
        newHistory,
        activeDimensions,
        nValue,
        interferenceRate
      );
      setCurrentStimulus(newStimulus);
      setCurrentTrial(prev => prev + 1);
    }, 3000 + initialDelay);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(hideTimer);
      clearTimeout(advanceTimer);
      // Stop any playing rhythm when trial ends
      if (activeDimensions.includes('rhythm')) {
        stopRhythm();
      }
    };
  }, [isPlaying, currentTrial, currentStimulus, activeDimensions, history, matchSchedule, nValue, playAudio, playNote, playToneWithVolume, playRhythm, stopRhythm, totalTrials, interferenceRate]);

  // Calculate level after session
  const calculateLevel = async (finalAccuracy = null) => {
    const accuracyToUse = finalAccuracy || accuracy;
    const result = calculateLevelRecommendation(accuracyToUse, activeDimensions, nValue);

    // Update the level for this specific dimension combination
    updateLevelForCombo(activeDimensions, result.newLevel);

    const summary = {
      dimensionResults: result.dimensionResults,
      currentLevel: nValue,
      newLevel: result.newLevel,
      recommendation: result.recommendation,
      dimensionCombo: [...activeDimensions],
      interferenceRate: interferenceRate,
      saved: false // Track if saved to server
    };
    
    // Save round to server BEFORE showing summary
    if (onRoundComplete) {
      try {
        console.log('Saving round to server:', {
          dimensions: activeDimensions,
          nValue: nValue,
          interferenceRate: interferenceRate,
          dimensionResults: result.dimensionResults
        });
        
        const saveResult = await onRoundComplete({
          dimensions: activeDimensions,
          nValue: nValue,
          interferenceRate: interferenceRate,
          dimensionResults: result.dimensionResults
        });
        
        console.log('Round save result:', saveResult);
        summary.saved = saveResult?.success !== false;
        
        // Mark as anonymous if applicable
        if (saveResult?.anonymous) {
          summary.anonymous = true;
        }
        
        // Handle limit errors
        if (saveResult?.limitReached) {
          summary.saved = false;
          summary.limitError = {
            type: saveResult.limitType,
            message: saveResult.error,
            usage: saveResult.usage
          };
        }
      } catch (error) {
        console.error('Failed to save round:', error);
        summary.saved = false;
      }
    } else {
      // No callback provided, consider it saved locally
      summary.saved = true;
    }
    
    // Set summary and show it AFTER save completes
    setRoundSummary(summary);
    setShowSummary(true);
    setShowSettings(false);
  };

  // Start game
  const startGame = () => {
    if (activeDimensions.length === 0) {
      alert('Please select at least one dimension!');
      return;
    }

    // Use the current nValue (which may be manually set via slider)
    // Don't override with recommended level here
    setTotalTrials(nValue + opportunityTrials);
    
    const schedule = generateMatchSchedule(activeDimensions, opportunityTrials);
    setMatchSchedule(schedule);
    
    setShowSettings(false);
    setCurrentTrial(0);
    setHistory([]);
    setResponses({});
    responsesRef.current = {};
    setAccuracy({});
    accuracyRef.current = {};
    setStimulusVisible(true);
    
    const newStimulus = generateStimulus(
      0,
      schedule,
      [],
      activeDimensions,
      nValue,
      interferenceRate
    );
    setCurrentStimulus(newStimulus);
    setCountdown(5);
  };

  // Reset game - go back to settings
  const resetGame = async () => {
    setIsPlaying(false);
    setShowSummary(false);
    setRoundSummary(null);
    setCountdown(null);
    setCurrentTrial(0);
    setHistory([]);
    setResponses({});
    responsesRef.current = {};
    setAccuracy({});
    accuracyRef.current = {};
    setMatchSchedule({});
    setStimulusVisible(true);
    setPressedInCurrentTrial(new Set());
    
    // Update nValue to the recommended level for current combo
    const currentKey = getDimensionComboKey(activeDimensions);
    const recommendedLevel = dimensionLevels[currentKey] || 1;
    setNValue(recommendedLevel);
    
    setShowSettings(true);
    
    // Reload progress data from server to get updated combo list
    if (onReturnToSettings) {
      await onReturnToSettings();
    }
  };

  // Continue to next round
  const continueTraining = () => {
    const newNValue = roundSummary ? roundSummary.newLevel : nValue;
    
    setNValue(newNValue);
    setTotalTrials(newNValue + opportunityTrials);
    
    const schedule = generateMatchSchedule(activeDimensions, opportunityTrials);
    setMatchSchedule(schedule);
    
    setShowSummary(false);
    setShowSettings(false);
    setCurrentTrial(0);
    setHistory([]);
    setResponses({});
    responsesRef.current = {};
    setAccuracy({});
    accuracyRef.current = {};
    setStimulusVisible(true);
    
    const newStimulus = generateStimulus(
      0,
      schedule,
      [],
      activeDimensions,
      newNValue,
      interferenceRate
    );
    setCurrentStimulus(newStimulus);
    setCountdown(5);
  };

  // Toggle dimension
  const toggleDimension = (dimension) => {
    setActiveDimensions(prev => {
      const isCurrentlyActive = prev.includes(dimension);
      
      if (isCurrentlyActive) {
        return prev.filter(d => d !== dimension);
      } else {
        let newDimensions = [...prev, dimension];
        
        // Musical note is mutually exclusive with audio and voice
        if (dimension === 'note') {
          newDimensions = newDimensions.filter(d => d !== 'audio' && d !== 'voice' && d !== 'categoryAudio' && d !== 'nameAudio' && d !== 'mandarinTone');
        }
        
        // Audio and voice are mutually exclusive with note, rhythm, and mandarinTone
        if (dimension === 'audio' || dimension === 'voice') {
          newDimensions = newDimensions.filter(d => d !== 'note' && d !== 'categoryAudio' && d !== 'nameAudio' && d !== 'rhythm' && d !== 'mandarinTone');
        }
        
        // Category/Name Audio are mutually exclusive with audio, voice, note, rhythm, mandarinTone, and each other
        if (dimension === 'categoryAudio') {
          newDimensions = newDimensions.filter(d => d !== 'audio' && d !== 'voice' && d !== 'note' && d !== 'nameAudio' && d !== 'rhythm' && d !== 'mandarinTone');
        }
        if (dimension === 'nameAudio') {
          newDimensions = newDimensions.filter(d => d !== 'audio' && d !== 'voice' && d !== 'note' && d !== 'categoryAudio' && d !== 'rhythm' && d !== 'mandarinTone');
        }
        
        // Rhythm is mutually exclusive with audio, voice, categoryAudio, nameAudio, mandarinTone (only pairs with note)
        if (dimension === 'rhythm') {
          newDimensions = newDimensions.filter(d => d !== 'audio' && d !== 'voice' && d !== 'categoryAudio' && d !== 'nameAudio' && d !== 'mandarinTone');
        }
        
        // Mandarin Tone is mutually exclusive with audio, voice, note, rhythm, categoryAudio, nameAudio
        if (dimension === 'mandarinTone') {
          newDimensions = newDimensions.filter(d => d !== 'audio' && d !== 'voice' && d !== 'note' && d !== 'rhythm' && d !== 'categoryAudio' && d !== 'nameAudio');
        }
        
        // Category/Name Text are mutually exclusive with each other (but not with other dimensions)
        if (dimension === 'categoryText') {
          newDimensions = newDimensions.filter(d => d !== 'nameText');
        }
        if (dimension === 'nameText') {
          newDimensions = newDimensions.filter(d => d !== 'categoryText');
        }
        
        return newDimensions;
      }
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      color: '#e2e8f0',
      fontFamily: '"DM Sans", system-ui, sans-serif'
    }}>
      {/* Settings Panel */}
      {showSettings && !showSummary && !isPlaying && (
        <div style={{
          padding: window.innerWidth < 768 ? '1rem' : '2rem',
          fontFamily: '"DM Sans", system-ui, sans-serif'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <SettingsPanel
              activeDimensions={activeDimensions}
              toggleDimension={toggleDimension}
              nValue={nValue}
              setNValue={setNValue}
              interferenceRate={interferenceRate}
              setInterferenceRate={setInterferenceRate}
              startGame={startGame}
              voiceMap={voiceMap}
              getLevelForCombo={getLevelForCombo}
              updateLevelForCombo={updateLevelForCombo}
              trainedCombos={trainedCombos}
              setActiveDimensions={setActiveDimensions}
              favorites={favorites}
              toggleFavorite={toggleFavorite}
              isAuthenticated={isAuthenticated}
              hasRecentSession={progressData?.profile?.metadata?.lastTrainingDate ? 
                (new Date() - new Date(progressData.profile.metadata.lastTrainingDate)) < 3600000 : false}
            />
          </div>
        </div>
      )}

      {/* Countdown Screen */}
      {countdown !== null && (
        <div style={{
          padding: '2rem',
          fontFamily: '"DM Sans", system-ui, sans-serif'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <CountdownScreen
              countdown={countdown}
              activeDimensions={activeDimensions}
              nValue={nValue}
            />
          </div>
        </div>
      )}

      {/* Game Area */}
      {isPlaying && !showSummary && (
        <div style={{
          padding: '2rem',
          fontFamily: '"DM Sans", system-ui, sans-serif'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <GameArea
              activeDimensions={activeDimensions}
              currentStimulus={currentStimulus}
              stimulusVisible={stimulusVisible}
              resetGame={resetGame}
              nValue={nValue}
              onDimensionPress={handleDimensionPress}
              pressedInCurrentTrial={pressedInCurrentTrial}
            />
          </div>
        </div>
      )}

      {/* Round Summary */}
      {showSummary && roundSummary && !showSettings && !isPlaying && (
        <div style={{
          padding: '2rem',
          fontFamily: '"DM Sans", system-ui, sans-serif'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <RoundSummary
              roundSummary={roundSummary}
              continueTraining={continueTraining}
              resetGame={resetGame}
            />
          </div>
        </div>
      )}

      <style>{globalStyles}</style>
    </div>
  );
};

export default NBackGame;

